import React, { useEffect, useState } from "react";
import {
  addPincode,
  createRegion,
  fetchRegion,
  fetchRegions,
  removePincode,
  updatePincode,
  updateRegion,
} from "../../services/regionApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const formatCommission = (rate) => {
  const pct = Number(rate ?? 0) * 100;
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`;
};

const toPercentInput = (rate) => {
  const value = Number(rate ?? 0);
  return String(value <= 1 ? value * 100 : value);
};

const Regions = () => {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", commissionRate: "", convenienceFeePercent: "", isActive: true });
  const [pincode, setPincode] = useState("");
  const [areaName, setAreaName] = useState("");
  const [pinLat, setPinLat] = useState("");
  const [pinLng, setPinLng] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingPincodeId, setEditingPincodeId] = useState(null);
  const [editAreaName, setEditAreaName] = useState("");
  const [editLat, setEditLat] = useState("");
  const [editLng, setEditLng] = useState("");

  const loadRegions = async () => {
    setLoading(true);
    setError("");
    try {
      setRegions(await fetchRegions());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRegions(); }, []);

  const selectRegion = async (regionId) => {
    try {
      setSelectedRegion(await fetchRegion(regionId));
      setEditingPincodeId(null);
      setEditAreaName("");
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setForm({ name: "", commissionRate: "", convenienceFeePercent: "", isActive: true });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      commissionRate: parseFloat(form.commissionRate),
      convenienceFeePercent: form.convenienceFeePercent === ""
        ? 0
        : parseFloat(form.convenienceFeePercent),
      isActive: form.isActive,
    };
    try {
      if (editingId) await updateRegion(editingId, payload);
      else await createRegion(payload);
      resetForm();
      await loadRegions();
      if (selectedRegion?.id === editingId) {
        await selectRegion(editingId);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (region) => {
    setEditingId(region.id);
    setForm({
      name: region.name,
      commissionRate: toPercentInput(region.commissionRate),
      convenienceFeePercent: toPercentInput(region.convenienceFeePercent ?? 0),
      isActive: region.isActive,
    });
  };

  const handleAddPincode = async (e) => {
    e.preventDefault();
    if (!selectedRegion || pincode.length !== 6) {
      setError("Enter a valid 6-digit pincode");
      return;
    }
    if (!areaName.trim()) {
      setError("Area name is required (e.g. Koramangala)");
      return;
    }
    try {
      await addPincode(selectedRegion.id, {
        pincode,
        areaName: areaName.trim(),
        isServiceable: true,
        latitude: pinLat ? parseFloat(pinLat) : undefined,
        longitude: pinLng ? parseFloat(pinLng) : undefined,
      });
      setPincode("");
      setAreaName("");
      setPinLat("");
      setPinLng("");
      setError("");
      await selectRegion(selectedRegion.id);
      await loadRegions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemovePincode = async (pincodeId) => {
    if (!window.confirm("Remove this pincode?")) return;
    try {
      await removePincode(pincodeId);
      await selectRegion(selectedRegion.id);
      await loadRegions();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEditPincodeArea = (p) => {
    setEditingPincodeId(p.id);
    setEditAreaName(p.areaName || "");
    setEditLat(p.latitude != null ? String(p.latitude) : "");
    setEditLng(p.longitude != null ? String(p.longitude) : "");
  };

  const savePincodeArea = async (pincodeId) => {
    if (!editAreaName.trim()) {
      setError("Area name is required");
      return;
    }
    try {
      await updatePincode(pincodeId, {
        areaName: editAreaName.trim(),
        latitude: editLat ? parseFloat(editLat) : undefined,
        longitude: editLng ? parseFloat(editLng) : undefined,
      });
      setEditingPincodeId(null);
      setEditAreaName("");
      setEditLat("");
      setEditLng("");
      setError("");
      await selectRegion(selectedRegion.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const pincodeCount = (region) => {
    if (selectedRegion?.id === region.id) {
      return selectedRegion.pincodes?.length || 0;
    }
    return region.pincodeCount ?? "—";
  };

  return (
    <div className="admin-page">
      <h2>Service regions</h2>
      <p className="subtitle">
        Set commission and convenience fee per region. Convenience fee is a percentage
        of the commission amount only (not the device sale price). Example: commission ₹100
        + 1% convenience fee → hold/capture ₹101. Add pincodes with optional lat/lng for assignment.
      </p>
      {error && <p className="error-msg">{error}</p>}

      <div className="admin-split">
        <div className="admin-card">
          <h3>{editingId ? "Edit region" : "Create region"}</h3>
          <form className="admin-form" onSubmit={handleSubmit}>
            <label className="field-label">
              Region name
              <input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </label>
            <label className="field-label">
              Commission rate (%)
              <input type="number" step="0.01" min="0"
                value={form.commissionRate}
                onChange={(e) => setForm({ ...form, commissionRate: e.target.value })} required />
            </label>
            <label className="field-label">
              Convenience fee (% of commission)
              <input type="number" step="0.01" min="0"
                value={form.convenienceFeePercent}
                placeholder="e.g. 1 for 1% of commission"
                onChange={(e) => setForm({ ...form, convenienceFeePercent: e.target.value })} />
            </label>
            <label className="checkbox-field">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Active
            </label>
            <div className="form-actions">
              <button type="submit" className="admin-btn">{editingId ? "Update" : "Create"}</button>
              {editingId && (
                <button type="button" className="admin-btn secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h3>Regions</h3>
          {loading ? (
            <div className="admin-loading">Loading regions</div>
          ) : (
            <TableScroll>
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Commission</th><th>Conv. fee</th><th>Active</th><th>Pincodes</th><th></th></tr>
              </thead>
              <tbody>
                {regions.length === 0 && (
                  <tr><td colSpan={6} className="empty-msg">No regions yet</td></tr>
                )}
                {regions.map((region) => (
                  <tr key={region.id} className={selectedRegion?.id === region.id ? "row-selected" : undefined}>
                    <td>{region.name}</td>
                    <td>{formatCommission(region.commissionRate)}</td>
                    <td>{formatCommission(region.convenienceFeePercent)}</td>
                    <td className={region.isActive ? "badge-active" : "badge-inactive"}>
                      {region.isActive ? "Yes" : "No"}
                    </td>
                    <td>{pincodeCount(region)}</td>
                    <td className="actions-cell">
                      <button type="button" className="admin-btn secondary" onClick={() => selectRegion(region.id)}>Pincodes</button>
                      <button type="button" className="admin-btn secondary" onClick={() => startEdit(region)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          )}
        </div>
      </div>

      {selectedRegion && (
        <div className="admin-card">
          <h3>
            Pincodes — {selectedRegion.name}
            <span className="subtitle-inline"> · Commission {formatCommission(selectedRegion.commissionRate)}</span>
          </h3>
          <form className="admin-filters" onSubmit={handleAddPincode}>
            <label className="field-label">
              Pincode
              <input maxLength={6} value={pincode} required
                placeholder="560001"
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} />
            </label>
            <label className="field-label">
              Area name
              <input value={areaName} required
                placeholder="e.g. Koramangala"
                onChange={(e) => setAreaName(e.target.value)} />
            </label>
            <label className="field-label">
              Latitude
              <input type="number" step="any" value={pinLat}
                placeholder="11.0168"
                onChange={(e) => setPinLat(e.target.value)} />
            </label>
            <label className="field-label">
              Longitude
              <input type="number" step="any" value={pinLng}
                placeholder="76.9558"
                onChange={(e) => setPinLng(e.target.value)} />
            </label>
            <button type="submit" className="admin-btn">Add pincode</button>
          </form>
          <TableScroll>
          <table className="admin-table">
            <thead>
              <tr><th>Pincode</th><th>Area</th><th>Lat / Lng</th><th>Commission</th><th>Serviceable</th><th></th></tr>
            </thead>
            <tbody>
              {(selectedRegion.pincodes || []).length === 0 && (
                <tr><td colSpan={6} className="empty-msg">No pincodes yet — add pincode + area name above</td></tr>
              )}
              {(selectedRegion.pincodes || []).map((p) => (
                <tr key={p.id}>
                  <td>{p.pincode}</td>
                  <td>
                    {editingPincodeId === p.id ? (
                      <input
                        value={editAreaName}
                        onChange={(e) => setEditAreaName(e.target.value)}
                        placeholder="Area name"
                      />
                    ) : (
                      p.areaName || "—"
                    )}
                  </td>
                  <td>
                    {editingPincodeId === p.id ? (
                      <span style={{ display: "flex", gap: 6 }}>
                        <input
                          style={{ width: 90 }}
                          type="number"
                          step="any"
                          value={editLat}
                          onChange={(e) => setEditLat(e.target.value)}
                          placeholder="lat"
                        />
                        <input
                          style={{ width: 90 }}
                          type="number"
                          step="any"
                          value={editLng}
                          onChange={(e) => setEditLng(e.target.value)}
                          placeholder="lng"
                        />
                      </span>
                    ) : (
                      p.latitude != null && p.longitude != null
                        ? `${Number(p.latitude).toFixed(4)}, ${Number(p.longitude).toFixed(4)}`
                        : "—"
                    )}
                  </td>
                  <td>{formatCommission(p.commissionRate ?? selectedRegion.commissionRate)}</td>
                  <td className={p.isServiceable ? "badge-active" : "badge-inactive"}>
                    {p.isServiceable ? "Yes" : "No"}
                  </td>
                  <td className="actions-cell">
                    {editingPincodeId === p.id ? (
                      <>
                        <button type="button" className="admin-btn" onClick={() => savePincodeArea(p.id)}>Save</button>
                        <button type="button" className="admin-btn secondary" onClick={() => setEditingPincodeId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="admin-btn secondary" onClick={() => startEditPincodeArea(p)}>Edit</button>
                        <button type="button" className="admin-btn danger" onClick={() => handleRemovePincode(p.id)}>Remove</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}
    </div>
  );
};

export default Regions;
